import { useQuery } from "@tanstack/react-query"
import { type SortingState } from "@tanstack/react-table"
import { type PresetColumnConfig } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import { type ProjectOverview as _ProjectOverview } from "@tokenterminal/tt-types"
import { fetchProjectsList } from "modules/screener/Table"

type UseProjectsListOptions = {
  marketSectors: string[]
  chains: string[]
  sorting: SortingState
  columns: PresetColumnConfig[]
  limit: number
  enabled?: boolean
}

const defaultOptions: UseProjectsListOptions = {
  marketSectors: [],
  chains: [],
  sorting: [
    {
      id: "market_cap_fully_diluted_max_latest",
      desc: true,
    },
  ],
  columns: [
    {
      field: "market_cap_fully_diluted_max_latest",
      display: "market_cap_fully_diluted_max_latest",
      header: "Market Cap",
    },
    { field: "market_sectors" },
    { field: "fees_7d_sum" },
    { field: "price_7d_latest" },
    { field: "market_cap_fully_diluted_max_latest" },
    { field: "user_dau_max_latest" },
    { field: "user_dau_30d_change" },
    { field: "market_cap_fully_diluted_30d_change" },
    { field: "net_deposits_30d_latest" },
  ],
  limit: 1000,
}

/**
 * Fetches a list of projects using the new screener API.
 * @example
 * const { data: projectsList } = useProjectsList({
 *   marketSectors: ["lending"],
 *   chains: ["ethereum"],
 *   sorting: [{ id: "market_cap_fully_diluted_max_latest", desc: true }],
 *   columns: [
 *     { field: "market_cap_fully_diluted_max_latest", display: "market_cap_fully_diluted_max_latest", header: "Market Cap" },
 *   ],
 *   limit: 1000,
 * })
 */

export type ProjectOverview = _ProjectOverview & {
  data_id: string
  slug: string
  name: string
  chains: string[]
  market_sectors: string[]
}

export const useProjectsList = (
  options: Partial<UseProjectsListOptions> = defaultOptions
) => {
  const {
    marketSectors = defaultOptions.marketSectors,
    chains = defaultOptions.chains,
    sorting = defaultOptions.sorting,
    columns = defaultOptions.columns,
    limit = defaultOptions.limit,
    enabled = true,
  } = options

  return useQuery<ProjectOverview[]>({
    queryKey: ["all-projects", marketSectors, chains, sorting, columns, limit],
    queryFn: async () => {
      const result = await fetchProjectsList(
        {
          columns,
          sorting,
          search: "",
          marketSectors,
          chains,
        },
        limit,
        0
      )
      return result.data as ProjectOverview[]
    },
    staleTime: 1000 * 60 * 10,
    enabled,
  })
}
